// TelcoTrack - Topbar section tabs service
// Feature pages register contextual section tabs here; the app shell renders the current level in the sidebar.

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import type { TabItem } from '../../shared/components/ui/tabs/tabs.component';
import { areShellTabItemsEqual } from '../navigation/shell-nav';

export interface TopbarSectionTabRow {
  ownerId: symbol;
  tabs: TabItem[];
  activeId: string;
  ariaLabel: string;
  idPrefix: string;
  visible: boolean;
  onActiveIdChange: (tabId: string) => void;
}

export interface TopbarSectionTabsState {
  rows: TopbarSectionTabRow[];
}

@Injectable({
  providedIn: 'root',
})
export class TopbarSectionTabsService {
  private readonly registrations = new Map<symbol, TopbarSectionTabRow>();
  private readonly stateSubject = new BehaviorSubject<TopbarSectionTabsState>({ rows: [] });
  readonly state$ = this.stateSubject.asObservable();

  registerRow(row: TopbarSectionTabRow): void {
    // Mutate in place and preserve `existing.tabs` when items are value-equal so
    // sidebar `*ngFor` buttons are never destroyed/recreated across change detection.
    const existing = this.registrations.get(row.ownerId);
    if (existing) {
      const tabsChanged = !areShellTabItemsEqual(existing.tabs, row.tabs);
      const stateChanged =
        tabsChanged ||
        existing.activeId !== row.activeId ||
        existing.ariaLabel !== row.ariaLabel ||
        existing.idPrefix !== row.idPrefix ||
        existing.visible !== row.visible;
      if (tabsChanged) {
        existing.tabs = row.tabs;
      }
      existing.activeId = row.activeId;
      existing.ariaLabel = row.ariaLabel;
      existing.idPrefix = row.idPrefix;
      existing.visible = row.visible;
      existing.onActiveIdChange = row.onActiveIdChange;
      if (!stateChanged) {
        return;
      }
    } else {
      this.registrations.set(row.ownerId, row);
    }
    this.emit();
  }

  unregisterRow(ownerId: symbol): void {
    this.registrations.delete(ownerId);
    this.emit();
  }

  clearAll(): void {
    this.registrations.clear();
    this.emit();
  }

  /**
   * Tell the page which tab was chosen.
   * Returning early when `activeId` already matched dropped the click: the next
   * change detection wrote the old id back, so the row never navigated.
   */
  selectTab(ownerId: symbol, tabId: string): boolean {
    const row = this.registrations.get(ownerId);
    if (!row) return false;
    const tab = row.tabs.find((item) => item.id === tabId);
    if (!tab || tab.disabled) return false;
    if (row.activeId !== tabId) {
      row.activeId = tabId;
      this.emit();
    }
    row.onActiveIdChange(tabId);
    return true;
  }

  private emit(): void {
    const rows = Array.from(this.registrations.values()).filter(
      (row) => row.visible !== false && row.tabs.length > 0,
    );
    this.stateSubject.next({ rows });
  }
}
